import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import { Box, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import { MIcon } from '../@material-extend';
import LoadingScreen from '../loadings/LoadingScreen';
import { GenericModal } from '../modals';

import useAuth from '../../hooks/useAuth';

const ContentFile = ({ fileUrl, loading, path }) => {
  if (loading) {
    return <LoadingScreen />;
  }

  const fileRewriteUrl = () => {
    if (!fileUrl) {
      return;
    }
    const extension = path.substring(path.lastIndexOf('.'));
    switch (extension) {
      case '.docx':
      case '.doc':
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
      default:
        return `${fileUrl}#zoom=100&toolbar=1&statusbar=1&navpanes=1`;
    }
  };

  return <iframe title="documentComete" src={fileRewriteUrl()} height="100%" width="100%" />;
};
ContentFile.propTypes = {
  path: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  fileUrl: PropTypes.string
};

const LinkFile = ({ smallDevice, fileUrl, path, findFile, onOpenFile }) => {
  const formattedPath = path.split('\\').pop();

  const handleClick = () => {
    if (smallDevice) {
      return downloadFile(fileUrl);
    }
    onOpenFile(true);
  };

  if (!findFile) {
    return (
      <Stack direction="row">
        <MIcon icon="attach_file" />
        <Typography sx={{ wordBreak: 'break-word' }}>{formattedPath}</Typography>
      </Stack>
    );
  }

  function downloadFile(url) {
    if (!url) {
      return false;
    }
    window.open(url, '_blank');
    return true;
  }

  return (
    <Link sx={{ display: 'flex', flexDirection: 'row', cursor: 'pointer', color: 'inherit' }} onClick={handleClick}>
      <MIcon icon="attach_file" />
      <Typography sx={{ wordBreak: 'break-word' }}>{formattedPath}</Typography>
    </Link>
  );
};
LinkFile.propTypes = {
  path: PropTypes.string.isRequired,
  findFile: PropTypes.bool.isRequired,
  smallDevice: PropTypes.bool.isRequired,
  onOpenFile: PropTypes.func.isRequired,
  fileUrl: PropTypes.string
};

const AttachedFile = ({ path }) => {
  const { get } = useAuth();
  const theme = useTheme();
  const smallDevice = useMediaQuery(theme.breakpoints.down('md'));
  const mounted = useRef(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [openFile, setOpenFile] = useState(false);
  const [findFile, setFindFile] = useState(false);
  const [loading, setLoading] = useState(true);

  async function fetchData() {
    const result = await get('/demands/file', { path });
    return result;
  }

  useEffect(() => {
    mounted.current = true;

    fetchData()
      .then((response) => {
        if (mounted.current) {
          if (response.success && response.url) {
            setFindFile(true);
            setLoading(false);
            const { url } = response;
            setFileUrl(url);
          } else {
            setFindFile(false);
            setLoading(false);
          }
        }
      })
      .catch(() => {
        if (mounted.current) {
          setFindFile(false);
          setLoading(false);
        }
      });

    return () => {
      mounted.current = false;
    };
  }, [path]);

  return (
    <>
      <LinkFile
        path={path}
        fileUrl={fileUrl}
        smallDevice={smallDevice}
        findFile={findFile}
        onOpenFile={() => setOpenFile(true)}
      />

      {openFile && (
        <GenericModal title="Détail du document" url={fileUrl} open={openFile} handleClose={() => setOpenFile(false)}>
          <Box sx={{ height: '90vh', overflow: 'hidden' }}>
            <ContentFile path={path} fileUrl={fileUrl} loading={loading} />
          </Box>
        </GenericModal>
      )}
    </>
  );
};
AttachedFile.propTypes = {
  path: PropTypes.string.isRequired
};

export default AttachedFile;
